import { useEffect, useRef, useState } from "react";
import {
  Sparkles,
  Send,
  Loader2,
  ExternalLink,
  FileDown,
  FileSpreadsheet,
  Plus,
  ClipboardList,
  Trash2,
  Info,
  Target,
  FileText,
  Download,
  X,
  Copy,
  Maximize2,
  Minimize2,
  MessageSquare,
} from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";

// Allow **bold** only — strip all other markdown before rendering (belt-and-braces; backend already does this)
function sanitiseText(raw) {
  if (!raw) return raw;
  const boldPh = {};
  let t = String(raw);
  // protect **bold** pairs (placeholder must not contain _ or * or other markdown)
  t = t.replace(/\*\*(.+?)\*\*/g, (m, inner) => {
    const k = `§BOLD${Object.keys(boldPh).length}§`;
    boldPh[k] = `**${inner}**`;
    return k;
  });
  t = t.replace(/^\s*#{1,6}\s+/gm, "");
  t = t.replace(/`([^`]*)`/g, "$1");
  t = t.replace(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g, "$1");
  t = t.replace(/_(.+?)_/g, "$1");
  t = t.replace(/^\s*[-*]\s+/gm, "");
  t = t.replace(/^\s*\d+\.\s+/gm, "");
  t = t.replace(/^\s*>\s*/gm, "");
  // drop markdown table separators, replace pipes with space
  t = t
    .split("\n")
    .filter((line) => !/^\s*\|?[\s\-:|]+\|?\s*$/.test(line))
    .map((line) => (line.split("|").length >= 3 ? line.replace(/\|/g, " ") : line))
    .join("\n");
  t = t.replace(/^\s*Sources:\s*.*$/gim, "");
  t = t.replace(/\n{3,}/g, "\n\n");
  Object.entries(boldPh).forEach(([k, v]) => {
    t = t.split(k).join(v);
  });
  return t.trim();
}

function relTime(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const diff = Date.now() - then;
  const m = Math.round(diff / 60000);
  if (m < 1) return "Just now";
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d === 1) return "Yesterday";
  if (d < 30) return `${d} days ago`;
  return new Date(iso).toLocaleDateString();
}

const STARTERS = [
  "Summarise everything that changed this week",
  "Which tenders should I act on first?",
  "Compare my top news items in a table",
];

// How often to poll for the background answer, and how long to keep trying.
// Backend LLM call has timeout 75s + queue/DB overhead, so frontend must poll longer
// than the backend or it will show "taking longer" while the answer is still
// being written — requiring the user to manually switch conversations to see it.
const POLL_INTERVAL_MS = 1200;
const POLL_TIMEOUT_MS = 90000;

// projectId: "" means All my projects (unified). Otherwise scoped to that project.
export default function AiAnalysisPanel({
  projectId,
  autoQuestion,
  autoNew,
  autoResultId,
  onConsumed,
}) {
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [aiQuestion, setAiQuestion] = useState("");
  const [sending, setSending] = useState(false);
  const [summarizing, setSummarizing] = useState(false);
  const [summaryText, setSummaryText] = useState("");
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [loadingSession, setLoadingSession] = useState(false);
  const [chatExpanded, setChatExpanded] = useState(false);
  const [expandedSources, setExpandedSources] = useState({});
  const [pendingDeleteSession, setPendingDeleteSession] = useState(null);
  const [focusedResultId, setFocusedResultId] = useState(null);
  const [focusedResult, setFocusedResult] = useState(null);
  const autoRan = useRef(false);
  const scrollRef = useRef(null);
  const mountedRef = useRef(false);
  // Tracks in-flight polling loops so we can cancel them (e.g. on new
  // conversation / unmount / switching sessions) and avoid stale updates.
  const pollTokenRef = useRef(0);
  const initializedProjectRef = useRef(false);
  const initializedProjectIdRef = useRef(undefined);

  const loadSessions = async () => {
    try {
      const r = await api.get("/assistant/sessions", {
        params: projectId ? { project_id: projectId } : {},
      });
      setSessions(r.data?.sessions || []);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    if (!focusedResultId) {
      setFocusedResult(null);
      return;
    }
    let cancelled = false;
    api.get(`/results/${focusedResultId}`).then((r) => {
      if (cancelled) return;
      setFocusedResult({ title: r.data?.title || "this article", url: r.data?.url || "" });
    }).catch(() => {
      if (!cancelled) setFocusedResult({ title: "this article", url: "" });
    });
    return () => {
      cancelled = true;
    };
  }, [focusedResultId]);

  useEffect(() => {
    if (
      initializedProjectRef.current &&
      initializedProjectIdRef.current === projectId
    ) {
      return;
    }
    initializedProjectRef.current = true;
    initializedProjectIdRef.current = projectId;
    startNew();
    loadSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    if (scrollRef.current)
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  // Avoid invalidating a live poll during React Strict Mode effect replay.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const startNew = () => {
    pollTokenRef.current += 1; // invalidate any in-flight poll loop
    setSessionId(null);
    setFocusedResultId(null);
    setFocusedResult(null);
    setMessages([]);
    setSummaryText("");
    setAiQuestion("");
    setExpandedSources({});
    setSending(false);
  };

  // Poll GET /assistant/sessions/:sid until the message with id === msgId
  // reaches a terminal status ("complete" or "error"), then merge it in.
  const pollForAnswer = async (sid, msgId, myToken) => {
    const startedAt = Date.now();

    const mergePolledMessage = (message) => {
      setMessages((prev) => {
        const nextMessage = {
          id: message.id,
          role: "assistant",
          content: message.content || "",
          answer_blocks: message.answer_blocks || null,
          citations: message.citations || [],
          citation_refs: message.citation_refs || [],
          table: message.table || null,
          refused: message.refused,
          confidence: message.confidence,
          data_gaps: message.data_gaps || [],
          suggested_followups: message.suggested_followups || [],
          pending: false,
          error: message.status === "error",
        };
        const index = prev.findIndex((item) => item.id === msgId);
        if (index === -1) return [...prev, nextMessage];
        return prev.map((item, itemIndex) =>
          itemIndex === index ? { ...item, ...nextMessage } : item,
        );
      });
    };

    const tick = async () => {
      if (!mountedRef.current || pollTokenRef.current !== myToken) return;

      try {
        const r = await api.get(`/assistant/sessions/${sid}`, {
          params: { _poll: Date.now() },
          headers: { "Cache-Control": "no-cache" },
        });
        const found = (r.data?.messages || []).find((m) => m.id === msgId);

        if (found && found.status === "complete") {
          if (pollTokenRef.current !== myToken) return;
          mergePolledMessage(found);
          setSending(false);
          loadSessions();
          return;
        }

        if (found && found.status === "error") {
          if (pollTokenRef.current !== myToken) return;
          mergePolledMessage({
            ...found,
            content:
              found.content ||
              "Something went wrong generating this answer. Please try again.",
          });
          setSending(false);
          return;
        }
      } catch {
        // transient network hiccup — keep polling until timeout
      }

      if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
        if (pollTokenRef.current !== myToken) return;
        // One final fetch before giving up — backend timeout is 75s, so the
        // answer may have just arrived after our last tick. Without this the
        // user sees a timeout error and must manually switch conversations
        // (openSession does a fresh GET) to see the answer.
        try {
          const r2 = await api.get(`/assistant/sessions/${sid}`, {
            params: { _poll: Date.now() },
            headers: { "Cache-Control": "no-cache" },
          });
          const f2 = (r2.data?.messages || []).find((m) => m.id === msgId);
          if (f2 && f2.status === "complete") {
            if (pollTokenRef.current !== myToken) return;
            mergePolledMessage(f2);
            setSending(false);
            loadSessions();
            return;
          }
          if (f2 && f2.status === "error") {
            if (pollTokenRef.current !== myToken) return;
            mergePolledMessage({
              ...f2,
              content: f2.content || "Something went wrong generating this answer. Please try again.",
            });
            setSending(false);
            return;
          }
        } catch {
          // ignore and show timeout below
        }
        setMessages((prev) =>
          prev.map((m) =>
            m.id === msgId
              ? {
                  ...m,
                  content:
                    "This is taking longer than expected. Please try asking again — or open the conversation again from the left to refresh.",
                  error: true,
                  pending: false,
                }
              : m,
          ),
        );
        setSending(false);
        return;
      }

      setTimeout(tick, POLL_INTERVAL_MS);
    };

    tick();
  };

  const askAssistant = async (question, fresh = false, opts = {}) => {
    const text = (question ?? aiQuestion).trim();
    if (!text || sending) return;

    // Resolve result scope: explicit opt > focused session > autoResultId (for fresh)
    const effectiveResultId = opts.resultId ?? (fresh ? autoResultId || focusedResultId : focusedResultId) ?? null;

    // Invalidate any previous poll loop (e.g. leftover from a prior question)
    const myToken = ++pollTokenRef.current;

    setSending(true);
    setSummaryText("");
    const useSession = fresh ? null : sessionId;
    if (fresh) {
      setSessionId(null);
      if (effectiveResultId) setFocusedResultId(effectiveResultId);
      else if (!autoResultId) setFocusedResultId(null);
      setMessages([{ role: "user", content: text }]);
    } else {
      setMessages((m) => [...m, { role: "user", content: text }]);
    }
    setAiQuestion("");

    try {
      const body = {
        question: text,
        project_id: projectId || null,
        session_id: useSession,
      };
      if (effectiveResultId) body.result_id = effectiveResultId;
      const r = await api.post("/assistant/ask", body);
      const d = r.data || {};
      const isNewSession = d.session_id && d.session_id !== useSession;
      if (d.session_id) setSessionId(d.session_id);
      // The backend already persists the session (with the user's message,
      // so it gets a title) synchronously inside POST /ask — refresh the
      // sidebar right away instead of waiting for the answer to finish.
      if (isNewSession) loadSessions();

      if (d.status === "complete") {
        // e.g. the "no projects yet" fast-path — answer is already final.
        setMessages((m) => [
          ...m,
          {
            id: d.message_id || undefined,
            role: "assistant",
            content: d.answer,
            answer_blocks: d.answer_blocks || null,
            citations: d.citations || [],
            citation_refs: d.citation_refs || [],
            table: d.table || null,
            refused: d.refused,
          },
        ]);
        setSending(false);
        loadSessions();
        return;
      }

      // Otherwise the answer is being generated in the background.
      // Show a pending bubble and poll until it resolves.
      setMessages((m) => [
        ...m,
        {
          id: d.message_id,
          role: "assistant",
          content: "",
          citations: [],
          table: null,
          pending: true,
        },
      ]);
      pollForAnswer(d.session_id, d.message_id, myToken);
    } catch (e) {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content:
            e?.response?.data?.detail ||
            "Something went wrong. Please try again.",
          error: true,
        },
      ]);
      setSending(false);
    }
  };

  // Auto-run a question redirected from the floating Ask-AI bar (once).
  useEffect(() => {
    if (!autoRan.current && autoQuestion) {
      autoRan.current = true;
      // Preserve single-article focus for this fresh session
      if (autoResultId) setFocusedResultId(autoResultId);
      askAssistant(autoQuestion, true, { resultId: autoResultId || null });
      onConsumed && onConsumed();
    }
    // eslint-disable-next-line
  }, [autoQuestion, autoNew, autoResultId]);

  const openSession = async (id) => {
    if (id === sessionId) return;
    const myToken = ++pollTokenRef.current; // cancel any running poll loop
    setLoadingSession(true);
    setSummaryText("");
    setExpandedSources({});
    try {
      const r = await api.get(`/assistant/sessions/${id}`);
      setSessionId(id);
      setFocusedResultId(r.data?.result_id || null);
      const msgs = (r.data?.messages || []).map((m) => ({
        id: m.id,
        role: m.role,
        content: m.content,
        answer_blocks: m.answer_blocks || null,
        citations: m.citations || [],
        citation_refs: m.citation_refs || [],
        table: m.table || null,
        refused: m.refused,
        confidence: m.confidence,
        data_gaps: m.data_gaps || [],
        suggested_followups: m.suggested_followups || [],
        pending: m.status === "processing",
        error: m.status === "error",
      }));
      setMessages(msgs);

      // If we reopened a conversation mid-generation (e.g. after a page
      // reload), resume polling for the still-pending message.
      const stillPending = msgs.find((m) => m.pending);
      if (stillPending) {
        setSending(true);
        pollForAnswer(id, stillPending.id, myToken);
      } else {
        setSending(false);
      }
    } catch {
      toast.error("Couldn't open that conversation.");
    } finally {
      setLoadingSession(false);
    }
  };

  const deleteSession = (id, e) => {
    e.stopPropagation();
    const session = sessions.find((s) => s.id === id) || null;
    setPendingDeleteSession(session);
  };

  const confirmDeleteSession = async () => {
    if (!pendingDeleteSession) return;
    const id = pendingDeleteSession.id;

    try {
      await api.delete(`/assistant/sessions/${id}`);
      setSessions((s) => s.filter((x) => x.id !== id));
      if (id === sessionId) startNew();
      setPendingDeleteSession(null);
      toast.success("Conversation deleted");
    } catch {
      setPendingDeleteSession(null);
      toast.error("Could not delete conversation.");
    }
  };

  const doSummary = async () => {
    if (!sessionId || summarizing) return;
    setSummarizing(true);
    setSummaryText("");
    setSummaryOpen(true);
    try {
      const r = await api.post("/assistant/summary", { session_id: sessionId });
      setSummaryText(r.data?.summary || "No summary was generated.");
    } catch (e) {
      setSummaryOpen(false);
      toast.error(
        e?.response?.data?.detail || "Could not generate a summary yet.",
      );
    } finally {
      setSummarizing(false);
    }
  };

  const copySummary = async () => {
    try {
      await navigator.clipboard.writeText(summaryText);
      toast.success("Summary copied");
    } catch {
      toast.error("Could not copy");
    }
  };

  const downloadCSV = (table) => {
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [
      table.columns.map(esc).join(","),
      ...table.rows.map((r) => r.map(esc).join(",")),
    ];
    const blob = new Blob([lines.join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "launchradar-export.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const downloadXLSX = async (table) => {
    try {
      const r = await api.post(
        "/assistant/export-xlsx",
        {
          columns: table.columns,
          rows: table.rows,
          filename: "launchradar-export",
        },
        { responseType: "blob" },
      );
      const a = document.createElement("a");
      a.href = URL.createObjectURL(new Blob([r.data]));
      a.download = "launchradar-export.xlsx";
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      toast.error("Excel export failed — try CSV.");
    }
  };

  function renderFormattedText(text) {
    if (!text) return null;
    // Split on **bold** segments, keeping the delimiters
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, idx) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        return (
          <strong key={idx} className="font-semibold">
            {part.slice(2, -2)}
          </strong>
        );
      }
      return <span key={idx}>{part}</span>;
    });
  }

  function blocksContainTable(blocks, table) {
    if (!Array.isArray(blocks) || !table || !Array.isArray(table.columns) || !Array.isArray(table.rows)) {
      return false;
    }
    const columns = table.columns.map((c) => String(c));
    return blocks.some(
      (b) =>
        b &&
        b.type === "table" &&
        Array.isArray(b.columns) &&
        b.columns.map((c) => String(c)).join(" ") === columns.join(" ") &&
        Array.isArray(b.rows) &&
        table.rows.length === b.rows.length,
    );
  }

  function renderAnswerBlocks(blocks, msgIndex) {
    if (!Array.isArray(blocks) || blocks.length === 0) return null;
    return (
      <div className="space-y-3">
        {blocks.map((b, bi) => {
          const type = b.type;
          if (type === "paragraph") {
            return (
              <div key={bi} className="whitespace-pre-wrap leading-relaxed">
                {renderFormattedText(b.text || "")}
              </div>
            );
          }
          if (type === "bullets") {
            return (
              <div key={bi}>
                {b.title && <div className="font-semibold text-[#0F1F3D] mb-1">{sanitiseText(b.title)}</div>}
                <ul className="list-disc pl-5 space-y-1">
                  {(b.items || []).map((it, ii) => (
                    <li key={ii} className="leading-relaxed">
                      {renderFormattedText(it)}
                    </li>
                  ))}
                </ul>
              </div>
            );
          }
          if (type === "steps") {
            return (
              <div key={bi}>
                {b.title && <div className="font-semibold text-[#0F1F3D] mb-1">{sanitiseText(b.title)}</div>}
                <ol className="list-decimal pl-5 space-y-1">
                  {(b.items || []).map((it, ii) => (
                    <li key={ii} className="leading-relaxed">
                      {renderFormattedText(it)}
                    </li>
                  ))}
                </ol>
              </div>
            );
          }
          if (type === "timeline") {
            return (
              <div key={bi}>
                {b.title && <div className="font-semibold text-[#0F1F3D] mb-1">{sanitiseText(b.title)}</div>}
                <div className="space-y-1.5">
                  {(b.events || []).map((ev, ei) => (
                    <div key={ei} className="flex gap-2 text-xs">
                      <span className="font-semibold text-[#64748B] shrink-0">{ev.date}</span>
                      <span className="text-[#1E293B]">{renderFormattedText(ev.label)}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          }
          if (type === "callout") {
            const tone = b.tone || "info";
            const toneMap = {
              opportunity: "bg-[#EFF6FF] border-[#2563EB] text-[#1D4ED8]",
              risk: "bg-[#FEF2F2] border-[#EF4444] text-[#B91C1C]",
              deadline: "bg-[#FFFBEB] border-[#F59E0B] text-[#B45309]",
              info: "bg-[#F8FAFC] border-[#E2E8F0] text-[#1E293B]",
            };
            return (
              <div key={bi} className={`rounded-lg border px-3 py-2 text-xs font-medium ${toneMap[tone] || toneMap.info}`}>
                {renderFormattedText(b.text || "")}
              </div>
            );
          }
          if (type === "table") {
            const cols = b.columns || [];
            const rows = b.rows || [];
            if (!cols.length || !rows.length) return null;
            return (
              <div key={bi} data-testid={`ai-block-table-${msgIndex}-${bi}`}>
                <div className="overflow-x-auto rounded-lg border border-[#E2E8F0]">
                  <table className="w-full text-xs">
                    <thead className="bg-[#F1F5F9]">
                      <tr>
                        {cols.map((c, ci) => (
                          <th key={ci} className="text-left font-semibold text-[#334155] px-3 py-2 whitespace-nowrap">
                            {sanitiseText(String(c))}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row, ri) => (
                        <tr key={ri} className="border-t border-[#E2E8F0]">
                          {row.map((cell, cci) => (
                            <td key={cci} className="px-3 py-2 text-[#475569] align-top">
                              {renderFormattedText(String(cell ?? ""))}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="mt-1.5 flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => downloadCSV({ columns: cols, rows })}
                    className="inline-flex items-center gap-1 text-[11px] font-medium text-[#64748B] hover:text-[#0F1F3D]"
                    data-testid={`ai-block-table-csv-${msgIndex}-${bi}`}
                  >
                    <FileDown className="h-3 w-3" /> CSV
                  </button>
                  <button
                    type="button"
                    onClick={() => downloadXLSX({ columns: cols, rows })}
                    className="inline-flex items-center gap-1 text-[11px] font-medium text-[#64748B] hover:text-[#0F1F3D]"
                    data-testid={`ai-block-table-xlsx-${msgIndex}-${bi}`}
                  >
                    <FileSpreadsheet className="h-3 w-3" /> Excel
                  </button>
                </div>
              </div>
            );
          }
          return null;
        })}
      </div>
    );
  }
  return (
    <>
      <AlertDialog open={!!pendingDeleteSession} onOpenChange={(open) => !open && setPendingDeleteSession(null)}>
        <AlertDialogContent data-testid="ai-session-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete conversation?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingDeleteSession
                ? `This will permanently delete “${pendingDeleteSession.title || "this conversation"}”. This action cannot be undone.`
                : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteSession}
              className="bg-[#B91C1C] hover:bg-[#991B1B] text-white"
              data-testid="ai-session-delete-confirm"
            >
              Delete conversation
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div
        className={`grid grid-cols-1 ${
          chatExpanded
            ? "lg:grid-cols-[56px_minmax(0,1fr)]"
            : "lg:grid-cols-[280px_minmax(0,1fr)_260px]"
        } gap-4`}
        data-testid="ai-analysis-tab"
      >
      {/* LEFT — conversations sidebar / collapsed chat rail */}
      {chatExpanded ? (
        <aside
          className="rounded-2xl border border-[#E2E8F0] bg-white py-3 px-2 flex flex-col items-center max-h-[80vh] overflow-visible"
          data-testid="ai-conversations-collapsed"
        >
          {/* AI Analysis */}
          <button
            type="button"
            onClick={() => setChatExpanded(false)}
            aria-label="Collapse chat"
            title="Collapse chat"
            data-testid="ai-collapsed-ai"
            className="inline-flex items-center justify-center h-10 w-10 rounded-xl bg-[#0F1F3D] text-white shadow-sm hover:bg-[#172A4F] transition-colors"
          >
            <Sparkles className="h-5 w-5" />
          </button>

          {/* New conversation */}
          <button
            type="button"
            onClick={startNew}
            aria-label="New conversation"
            title="New conversation"
            data-testid="ai-new-conversation-collapsed"
            className="inline-flex items-center justify-center h-10 w-10 mt-2 rounded-xl bg-[#F8FAFC] text-[#0F1F3D] hover:bg-[#EEF4FF] transition-colors"
          >
            <Plus className="h-5 w-5" />
          </button>

          {/* Conversation separator */}
          <div
            className="w-8 border-t border-[#E2E8F0] my-3"
            aria-hidden="true"
          />

          {/* Recent conversations */}
          <div
            className="w-full flex flex-col items-center gap-1"
            data-testid="ai-conversation-list-collapsed"
          >
            {sessions.slice(0, 4).map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => openSession(s.id)}
                aria-label={s.title || "Conversation"}
                data-testid={`ai-conversation-collapsed-${s.id}`}
                className={`group relative inline-flex items-center justify-center h-10 w-10 shrink-0 rounded-xl transition-colors ${
                  s.id === sessionId
                    ? "bg-[#EEF4FF] text-[#0F1F3D]"
                    : "text-[#0F1F3D] hover:bg-[#F8FAFC]"
                }`}
              >
                <MessageSquare className="h-4 w-4" strokeWidth={1.8} />

                {/* Conversation tooltip */}
                <span
                  role="tooltip"
                  className="pointer-events-none absolute left-full top-1/2 z-50 ml-2 w-max max-w-[240px] -translate-y-1/2 rounded-lg bg-[#0F1F3D] px-3 py-2 text-left text-xs font-medium leading-snug text-white opacity-0 shadow-lg transition-opacity duration-150 group-hover:opacity-100 group-focus-visible:opacity-100"
                >
                  {s.title || "Conversation"}
                </span>
              </button>
            ))}
          </div>
        </aside>
      ) : (
        <aside
          className="rounded-2xl border border-[#E2E8F0] bg-white p-4 flex flex-col max-h-[80vh]"
          data-testid="ai-conversations"
        >
          <button
            onClick={startNew}
            data-testid="ai-new-conversation"
            className="flex items-center justify-between gap-2 w-full px-3 py-2.5 rounded-lg border border-[#0F1F3D] text-[#0F1F3D] text-sm font-semibold hover:bg-[#0F1F3D] hover:text-white transition-colors"
          >
            New conversation
            <Plus className="h-4 w-4" />
          </button>

          <div className="text-[10px] font-bold uppercase tracking-wide text-[#94A3B8] mt-4 mb-2">
            Recent conversations
          </div>
          <div
            className="flex-1 overflow-y-auto -mx-1 px-1 space-y-1"
            data-testid="ai-conversation-list"
          >
            {sessions.length === 0 ? (
              <p className="text-xs text-[#94A3B8] px-1 py-3">
                No conversations yet. Ask something to get started.
              </p>
            ) : (
              sessions.map((s) => (
                <button
                  key={s.id}
                  onClick={() => openSession(s.id)}
                  data-testid={`ai-conversation-${s.id}`}
                  className={`group w-full text-left rounded-lg px-3 py-2.5 transition-colors ${
                    s.id === sessionId
                      ? "bg-[#EEF4FF] border border-[#0F1F3D]/20"
                      : "hover:bg-[#F8FAFC] border border-transparent"
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-[13px] font-medium text-[#1E293B] line-clamp-2 leading-snug">
                      {s.title}
                    </p>
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => deleteSession(s.id, e)}
                      data-testid={`ai-conversation-delete-${s.id}`}
                      className="opacity-0 group-hover:opacity-100 text-[#CBD5E1] hover:text-[#EF4444] transition-opacity shrink-0 mt-0.5"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </span>
                  </div>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-[10px] text-[#94A3B8]">
                      {relTime(s.updated_at)}
                    </span>
                    {!projectId && (
                      <span className="text-[10px] text-[#94A3B8] truncate max-w-[110px]">
                        {s.scope_label}
                      </span>
                    )}
                  </div>
                </button>
              ))
            )}
          </div>
        </aside>
      )}

      {/* CENTER — chat */}
      <section
        className="rounded-2xl border border-[#E2E8F0] bg-white flex flex-col max-h-[80vh]"
        data-testid="ai-chat"
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#F1F5F9]">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-[#0F1F3D] text-white">
              <Sparkles className="h-3 w-3" />
            </span>
            <span className="text-sm font-bold text-[#0F1F3D]">
              {projectId ? "Project analysis" : "All my projects"}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {messages.length > 0 && sessionId && (
              <button
                onClick={doSummary}
                disabled={summarizing}
                data-testid="ai-analysis-summary"
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#0F1F3D] hover:underline disabled:opacity-50"
              >
                {summarizing ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <ClipboardList className="h-3.5 w-3.5" />
                )}
                Summary report
              </button>
            )}
            <button
              type="button"
              onClick={() => setChatExpanded((expanded) => !expanded)}
              aria-label={chatExpanded ? "Collapse chat" : "Expand chat"}
              aria-expanded={chatExpanded}
              data-testid="ai-analysis-expand"
              title={chatExpanded ? "Collapse chat" : "Expand chat"}
              className="inline-flex items-center justify-center h-8 w-8 rounded-lg text-[#64748B] hover:bg-[#F1F5F9] hover:text-[#0F1F3D] transition-colors"
            >
              {chatExpanded ? (
                <Minimize2 className="h-4 w-4" />
              ) : (
                <Maximize2 className="h-4 w-4" />
              )}
            </button>
          </div>
        </div>

        {focusedResultId && (
          <div
            className="mx-5 mt-3 px-3 py-2 rounded-lg bg-[#EEF4FF] border border-[#CBD5E1] flex items-center gap-2 text-xs"
            data-testid="ai-result-focus-banner"
          >
            <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-[#0F1F3D] text-white shrink-0">
              <Sparkles className="h-3 w-3" />
            </span>
            <span className="text-[#1E293B] font-medium truncate">
              Focused on{" "}
              {focusedResult?.url ? (
                <a
                  href={focusedResult.url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="ai-focus-article-link"
                  className="underline decoration-[#0F1F3D]/30 hover:decoration-[#0F1F3D] hover:text-[#0F1F3D] text-[#0F1F3D]"
                  title={focusedResult.title}
                >
                  {focusedResult.title}
                </a>
              ) : (
                <span className="text-[#0F1F3D]">{focusedResult?.title || "this article"}</span>
              )}
            </span>
          </div>
        )}

        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto px-5 py-4 space-y-3"
          data-testid="ai-analysis-messages"
        >
          {messages.length === 0 && !loadingSession ? (
            <div className="h-full flex flex-col items-center justify-center text-center py-10">
              <span className="inline-flex items-center justify-center h-12 w-12 rounded-2xl bg-[#EEF4FF] text-[#0F1F3D] mb-3">
                <Sparkles className="h-6 w-6" />
              </span>
              <h4 className="text-sm font-bold text-[#0F1F3D]">
                Ask about your intelligence
              </h4>
              <p className="text-xs text-[#64748B] max-w-xs mt-1 mb-4">
                Summaries, comparisons and weekly digests across your news,
                tenders and recommended actions.
              </p>
              <div className="flex flex-col items-stretch gap-2 w-full max-w-sm">
                {STARTERS.map((q, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => askAssistant(q, true)}
                    data-testid={`ai-analysis-starter-${i}`}
                    className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg text-xs font-medium border border-[#CBD5E1] bg-white text-[#334155] hover:border-[#0F1F3D] hover:text-[#0F1F3D] transition-colors"
                  >
                    <Sparkles className="h-3 w-3 text-[#2563EB]" /> {q}
                  </button>
                ))}
              </div>
            </div>
          ) : loadingSession ? (
            <div className="inline-flex items-center gap-2 text-xs text-[#64748B]">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading
              conversation…
            </div>
          ) : (
            messages.map((m, i) => (
              <div
                key={m.id || i}
                className={m.role === "user" ? "flex justify-end" : ""}
              >
                {m.role === "user" ? (
                  <div
                    className="max-w-[85%] rounded-2xl rounded-br-sm bg-[#0F1F3D] text-white px-4 py-2.5 text-sm"
                    data-testid={`ai-msg-user-${i}`}
                  >
                    {m.content}
                  </div>
                ) : (
                  //AI answer div
                  <div
                    className="max-w-full rounded-2xl rounded-bl-sm bg-[#F8FAFC] border border-[#E2E8F0] px-4 py-3 text-sm text-[#1E293B]"
                    data-testid={`ai-msg-assistant-${i}`}
                  >
                    {m.pending ? (
                      <div className="inline-flex items-center gap-2 text-xs text-[#64748B]">
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        Analysing your data…
                      </div>
                    ) : m.answer_blocks && Array.isArray(m.answer_blocks) && m.answer_blocks.length > 0 ? (
                      renderAnswerBlocks(m.answer_blocks, i)
                    ) : (
                      <div className="whitespace-pre-wrap leading-relaxed">
                        {renderFormattedText(m.content)}
                      </div>
                    )}

                    {!m.pending && m.table && Array.isArray(m.table.columns) && !blocksContainTable(m.answer_blocks, m.table) && (
                      <div className="mt-3" data-testid={`ai-table-${i}`}>
                        <div className="overflow-x-auto rounded-lg border border-[#E2E8F0]">
                          <table className="w-full text-xs">
                            <thead className="bg-[#F1F5F9]">
                              <tr>
                                {m.table.columns.map((c, ci) => (
                                  <th
                                    key={ci}
                                    className="text-left font-semibold text-[#334155] px-3 py-2 whitespace-nowrap"
                                  >
                                    {c}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {m.table.rows.map((row, ri) => (
                                <tr
                                  key={ri}
                                  className="border-t border-[#E2E8F0]"
                                >
                                  {row.map((cell, cci) => (
                                    <td
                                      key={cci}
                                      className="px-3 py-2 text-[#475569] align-top"
                                    >
                                      {cell}
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <div className="mt-2 flex gap-2">
                          <button
                            onClick={() => downloadCSV(m.table)}
                            data-testid={`ai-export-csv-${i}`}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[#E2E8F0] text-xs font-semibold text-[#475569] hover:bg-[#F1F5F9]"
                          >
                            <FileDown className="h-3.5 w-3.5" /> Export CSV
                          </button>
                          <button
                            onClick={() => downloadXLSX(m.table)}
                            data-testid={`ai-export-xlsx-${i}`}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[#E2E8F0] text-xs font-semibold text-[#475569] hover:bg-[#F1F5F9]"
                          >
                            <FileSpreadsheet className="h-3.5 w-3.5" /> Export
                            Excel
                          </button>
                        </div>
                      </div>
                    )}

                    {!m.pending && m.citations && m.citations.length > 0 && (
                      <div
                        className="mt-3 pt-2 border-t border-[#E2E8F0]"
                        data-testid={`ai-citations-${i}`}
                      >
                        <button
                          type="button"
                          onClick={() =>
                            setExpandedSources((expanded) => ({
                              ...expanded,
                              [i]: !expanded[i],
                            }))
                          }
                          aria-expanded={Boolean(expandedSources[i])}
                          data-testid={`ai-sources-toggle-${i}`}
                          className="text-[10px] font-bold uppercase tracking-wide text-[#64748B] hover:text-[#0F1F3D]"
                        >
                          Sources ({m.citations.length})
                        </button>
                        {expandedSources[i] && (
                          <div className="flex flex-col gap-1 mt-1">
                            {m.citations.map((c, ci) => (
                              <a
                                key={ci}
                                href={c.url}
                                target="_blank"
                                rel="noreferrer"
                                className="flex items-start gap-1.5 text-xs text-[#0F1F3D] hover:underline"
                              >
                                <span className="shrink-0 font-semibold text-[#64748B]">
                                  [{c.ref ?? ci + 1}]
                                </span>
                                <span
                                  className={`inline-flex shrink-0 items-center px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${c.kind === "tender" ? "bg-[#FFFBEB] text-[#B45309]" : "bg-[#EFF6FF] text-[#1D4ED8]"}`}
                                >
                                  {c.kind}
                                </span>
                                <span className="min-w-0 break-words">{c.title}</span>
                                <ExternalLink className="h-3 w-3 shrink-0 mt-0.5" />
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
          {summaryText && !summaryOpen && (
            <button
              onClick={() => setSummaryOpen(true)}
              className="text-xs font-semibold text-[#0F1F3D] hover:underline"
              data-testid="ai-analysis-summary-reopen"
            >
              View chat summary
            </button>
          )}
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            askAssistant();
          }}
          className="border-t border-[#F1F5F9] p-3 flex items-center gap-2"
        >
          <input
            value={aiQuestion}
            onChange={(e) => setAiQuestion(e.target.value)}
            placeholder={
              messages.length
                ? "Ask a follow-up…"
                : projectId
                  ? "Ask anything about this project…"
                  : "Ask anything about your data…"
            }
            data-testid="ai-analysis-input"
            disabled={sending}
            className="flex-1 px-4 py-2.5 rounded-lg border border-[#CBD5E1] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#0F1F3D]/20 disabled:opacity-60"
          />
          <button
            type="submit"
            data-testid="ai-analysis-ask"
            disabled={sending}
            aria-label="Ask AI"
            className="inline-flex items-center justify-center h-11 w-11 rounded-lg bg-[#0F1F3D] text-white hover:bg-[#172A4F] disabled:opacity-60"
          >
            {sending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </button>
        </form>
      </section>

      {/* RIGHT — about panel */}
      <aside
        className={`rounded-2xl border border-[#E2E8F0] bg-white px-5 py-[0.65rem] max-h-[80vh] ${
          chatExpanded ? "hidden" : ""
        }`}
        data-testid="ai-about-panel"
      >
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="h-4 w-4 text-[#0F1F3D]" />
          <h4 className="text-sm font-bold text-[#0F1F3D]">
            About Analysis
          </h4>
        </div>
        <p className="text-xs text-[#64748B] mb-4 leading-relaxed">
          Ask questions about your projects and get insights based on the
          intelligence we&apos;ve collected.
        </p>
        <div className="space-y-4">
          {[
            {
              icon: Target,
              title: "Data-aware",
              body: "Answers are based only on the data we've collected for the selected scope.",
            },
            {
              icon: FileText,
              title: "Citations",
              body: "Sources are included wherever possible.",
            },
            {
              icon: Download,
              title: "Export & share",
              body: "Export tables and generate reports from any conversation.",
            },
          ].map((f, i) => (
            <div key={i} className="flex gap-3">
              <span className="inline-flex items-center justify-center h-8 w-8 rounded-lg bg-[#F1F5F9] text-[#0F1F3D] shrink-0">
                <f.icon className="h-4 w-4" />
              </span>
              <div>
                <p className="text-[13px] font-semibold text-[#1E293B]">
                  {f.title}
                </p>
                <p className="text-xs text-[#64748B] leading-snug">{f.body}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-1 pt-4 border-t border-[#F1F5F9] flex items-start gap-2 text-[11px] text-[#94A3B8]">
          <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
          AI responses can make mistakes. Verify important information.
        </div>
      </aside>

      {/* Summary report modal */}
      {summaryOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          data-testid="ai-summary-modal"
          onClick={() => setSummaryOpen(false)}
        >
          <div className="absolute inset-0 bg-black/50" />
          <div
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
            className="relative bg-white rounded-2xl shadow-2xl w-full max-w-[640px] max-h-[80vh] flex flex-col"
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E2E8F0]">
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-[#0F1F3D] text-white">
                  <ClipboardList className="h-3.5 w-3.5" />
                </span>
                <h3 className="text-sm font-bold text-[#0F1F3D]">
                  Conversation summary
                </h3>
              </div>
              <button
                onClick={() => setSummaryOpen(false)}
                aria-label="Close"
                data-testid="ai-summary-close"
                className="p-2 rounded-md hover:bg-[#F1F5F9] text-[#64748B]"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="px-6 py-5 overflow-y-auto">
              {summarizing ? (
                <div
                  className="flex items-center gap-2 text-sm text-[#64748B] py-8 justify-center"
                  data-testid="ai-summary-loading"
                >
                  <Loader2 className="h-4 w-4 animate-spin" /> Generating your
                  summary…
                </div>
              ) : (
                <p
                  className="text-sm text-[#1E293B] leading-relaxed whitespace-pre-wrap"
                  data-testid="ai-summary-text"
                >
                  {summaryText}
                </p>
              )}
            </div>
            {!summarizing && summaryText && (
              <div className="px-6 py-3 border-t border-[#E2E8F0] flex justify-end">
                <button
                  onClick={copySummary}
                  data-testid="ai-summary-copy"
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm font-semibold text-[#475569] hover:bg-[#F1F5F9]"
                >
                  <Copy className="h-3.5 w-3.5" /> Copy
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      </div>
    </>
  );
}